/**
 * SettlementUploadPage Component
 * 정산서 업로드 페이지 — 협력사/라이더 정산서 업로드 및 이력 관리
 */
import React, { useState, useRef, useCallback } from 'react';
import { useSettlementUpload } from '../hooks/useSettlementUpload';
import { SettlementUploadType } from '../types/settlementUpload.types';
import { ITEMS_PER_PAGE } from '../constants/settlementUpload.constants';
import { usePagination } from '@/shared/hooks/usePagination';
import { useTableSort } from '@/shared/hooks/useTableSort';
import Loading from '@/shared/components/feedback/Loading';
import ConfirmModal from '@/shared/components/feedback/ConfirmModal';
import UploadSection from './UploadSection';
import UploadHistoryTable from './UploadHistoryTable';
import '@/shared/styles/common-table.css';
import '@/shared/styles/search-form.css';
import '../styles/SettlementUploadPage.css';

const SettlementUploadPage: React.FC = () => {
    const {
        loading,
        uploadLoading,
        applyLoading,
        histories,
        uploadSettlement,
        applyUpload,
        cancelUpload,
    } = useSettlementUpload();

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState<{ type: 'apply' | 'cancel'; uploadId: string } | null>(null);

    const partnerFileInputRef = useRef<HTMLInputElement>(null);
    const riderFileInputRef = useRef<HTMLInputElement>(null);

    const { sortConfig, sortedData: sortedHistories, handleSort } = useTableSort({
        data: histories || [],
        numericColumns: ['supplierCount', 'riderCount', 'deliveryFee', 'additionalSettlement', 'managementFee', 'supplyAmount', 'vatAmount', 'hourlyInsurance'],
        dateColumns: ['uploadDate', 'applyDate'],
    });

    const { currentPage, totalPages, currentItems, goToPage } = usePagination({
        items: sortedHistories,
        itemsPerPage: ITEMS_PER_PAGE,
    });

    const handlePartnerUploadClick = useCallback(() => partnerFileInputRef.current?.click(), []);
    const handleRiderUploadClick = useCallback(() => riderFileInputRef.current?.click(), []);

    const handleFileChange = useCallback(async (
        event: React.ChangeEvent<HTMLInputElement>,
        uploadType: SettlementUploadType
    ) => {
        const file = event.target.files?.[0];
        if (!file) return;
        await uploadSettlement(uploadType, file);
        event.target.value = '';
    }, [uploadSettlement]);

    const handleApplyClick = useCallback((uploadId: string) => {
        setConfirmAction({ type: 'apply', uploadId });
        setShowConfirmModal(true);
    }, []);

    const handleCancelClick = useCallback((uploadId: string) => {
        setConfirmAction({ type: 'cancel', uploadId });
        setShowConfirmModal(true);
    }, []);

    const handleConfirm = useCallback(async () => {
        if (!confirmAction) return;
        if (confirmAction.type === 'apply') {
            await applyUpload(confirmAction.uploadId);
        } else {
            await cancelUpload(confirmAction.uploadId);
        }
        setShowConfirmModal(false);
        setConfirmAction(null);
    }, [confirmAction, applyUpload, cancelUpload]);

    const handleModalCancel = useCallback(() => {
        setShowConfirmModal(false);
        setConfirmAction(null);
    }, []);

    if (loading && histories.length === 0) return <Loading message="업로드 이력을 불러오는 중..." />;
    if (uploadLoading) return <Loading message="정산서를 업로드하는 중..." />;
    if (applyLoading) return <Loading message="정산서를 적용하는 중..." />;

    return (
        <div className="settlement-upload-page">
            <div className="page-header">
                <h1 className="page-title">정산서 업로드</h1>
            </div>

            <UploadSection
                uploadLoading={uploadLoading}
                partnerFileInputRef={partnerFileInputRef}
                riderFileInputRef={riderFileInputRef}
                onPartnerUploadClick={handlePartnerUploadClick}
                onRiderUploadClick={handleRiderUploadClick}
                onFileChange={handleFileChange}
            />

            <UploadHistoryTable
                currentItems={currentItems}
                sortedHistories={sortedHistories}
                histories={histories}
                currentPage={currentPage}
                totalPages={totalPages}
                sortConfig={sortConfig}
                applyLoading={applyLoading}
                onSort={handleSort}
                onApplyClick={handleApplyClick}
                onPageChange={goToPage}
            />

            <ConfirmModal
                isOpen={showConfirmModal}
                title={confirmAction?.type === 'apply' ? '정산서 적용' : '업로드 취소'}
                message={
                    confirmAction?.type === 'apply'
                        ? '정산서를 적용하시겠습니까? 적용 후에는 취소할 수 없습니다.'
                        : '업로드를 취소하시겠습니까?'
                }
                confirmText={confirmAction?.type === 'apply' ? '적용' : '취소'}
                cancelText="닫기"
                onConfirm={handleConfirm}
                onCancel={handleModalCancel}
            />
        </div>
    );
};

export default SettlementUploadPage;
